
import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Plus, Trash2, AlertCircle, CheckCircle, UserPlus } from "lucide-react";
import { format } from "date-fns";

export default function AddStudentsToEA() {
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [subject, setSubject] = useState("");
  const [assignmentText, setAssignmentText] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [message, setMessage] = useState(null);
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  const isTeacher = user?.role === 'admin' || (user?.role === 'user' && user?.user_type === 'teacher');
  const teacherGradeLevel = user?.teacher_grade_level;
  const teacherName = user?.full_name;
  const teacherEmail = user?.email;

  const { data: students = [] } = useQuery({
    queryKey: ['students'],
    queryFn: () => base44.entities.Student.list(),
  });

  const { data: myManualAssignments = [] } = useQuery({
    queryKey: ['myManualAssignments', teacherEmail],
    queryFn: async () => {
      if (!teacherEmail) return [];
      return base44.entities.ManualAssignment.filter({ teacher_email: teacherEmail }, '-created_date');
    },
    enabled: !!teacherEmail,
  });

  // Filter students by teacher's grade level
  const myGradeStudents = students.filter(s => s.grade_level === teacherGradeLevel);

  const filteredStudents = myGradeStudents
    .filter(student =>
      student.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.student_id?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      // Sort alphabetically by last name
      const lastNameA = a.last_name?.toLowerCase() || '';
      const lastNameB = b.last_name?.toLowerCase() || '';
      return lastNameA.localeCompare(lastNameB);
    });

  // Get pending assignments (not pulled yet)
  const pendingAssignments = myManualAssignments.filter(a => a.status === 'pending');
  const todaysPending = pendingAssignments.filter(a => a.request_date === format(new Date(), 'yyyy-MM-dd'));

  const toggleStudentSelection = (studentId) => {
    setSelectedStudentIds(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const toggleSelectAll = () => {
    if (filteredStudents.length === 0) return; // Prevent selecting all if no students are displayed
    if (selectedStudentIds.length === filteredStudents.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(filteredStudents.map(s => s.student_id));
    }
  };

  const addMultipleAssignmentsMutation = useMutation({
    mutationFn: async ({ studentIds, subject, assignment }) => {
      const today = format(new Date(), 'yyyy-MM-dd');
      const studentsToAdd = [];
      const duplicates = [];

      for (const studentId of studentIds) {
        const student = students.find(s => s.student_id === studentId);
        if (!student) continue;

        // Check for duplicate: same student + same teacher + same day
        const existingToday = myManualAssignments.find(
          a => a.student_id === studentId && 
               a.teacher_email === teacherEmail && 
               a.request_date === today &&
               a.status === 'pending'
        );

        if (existingToday) {
          duplicates.push(`${student.first_name} ${student.last_name}`);
          continue;
        }

        studentsToAdd.push({
          student_id: student.student_id,
          student_name: `${student.first_name} ${student.last_name}`,
          student_first_name: student.first_name,
          student_last_name: student.last_name,
          grade_level: student.grade_level,
          student_email: student.email || '',
          f1g1_parent_phone: student.f1g1_parent_phone || '',
          f1g2_parent_phone: student.f1g2_parent_phone || '',
          f1g1_parent_email: student.f1g1_parent_email || '',
          f2g1_parent_phone: student.f2g1_parent_phone || '',
          f2g1_parent_email: student.f2g1_parent_email || '',
          teacher_name: teacherName,
          teacher_email: teacherEmail,
          subject: subject,
          assignment: assignment,
          request_date: today,
          status: 'pending'
        });
      }

      // Create all assignments
      for (const assignmentData of studentsToAdd) {
        await base44.entities.ManualAssignment.create(assignmentData);
      }

      return { added: studentsToAdd.length, duplicates };
    },
    onSuccess: ({ added, duplicates }) => {
      let msg = `✅ Added ${added} student${added !== 1 ? 's' : ''} to EA request list for ${subject}!`;
      if (duplicates.length > 0) {
        msg += `\n⚠️ Skipped ${duplicates.length} duplicate${duplicates.length !== 1 ? 's' : ''}: ${duplicates.join(', ')}`;
      }
      setMessage({
        type: 'success',
        text: msg
      });
      setSelectedStudentIds([]);
      setSubject("");
      setAssignmentText("");
      setSearchTerm("");
      queryClient.invalidateQueries(['myManualAssignments']);
      setTimeout(() => setMessage(null), 7000);
    },
    onError: (err) => {
      setMessage({
        type: 'error',
        text: err.message || "Failed to add students"
      });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const deleteAssignmentMutation = useMutation({
    mutationFn: async (assignmentId) => {
      return base44.entities.ManualAssignment.delete(assignmentId);
    },
    onSuccess: () => {
      setMessage({
        type: 'success',
        text: "✅ Request removed successfully"
      });
      queryClient.invalidateQueries(['myManualAssignments']);
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      setMessage({
        type: 'error',
        text: err.message || "Failed to remove request"
      });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (selectedStudentIds.length === 0) {
      setMessage({ type: 'error', text: "Please select at least one student" });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    if (!subject) {
      setMessage({ type: 'error', text: "Please select a subject" });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (!assignmentText.trim()) {
      setMessage({ type: 'error', text: "Please enter an assignment description" });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    addMultipleAssignmentsMutation.mutate({
      studentIds: selectedStudentIds,
      subject: subject,
      assignment: assignmentText
    });
  };

  if (!isTeacher) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen flex items-center justify-center">
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            You do not have permission to access this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!teacherGradeLevel) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <div className="max-w-5xl mx-auto space-y-6">
          <h1 className="text-3xl font-bold text-slate-900">Add Students to EA</h1>
          <Alert variant="destructive">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              <strong>Grade Level Not Set!</strong> Your account doesn't have a grade level assigned. Please contact an administrator to set your grade level before you can manually add students to EA.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
            <UserPlus className="w-6 h-6 text-green-700" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Add Students to EA</h1>
            <p className="text-slate-600 mt-1">Manually Assign Students to Eagle Academy</p>
          </div>
        </div>

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'} style={{ whiteSpace: 'pre-line' }}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <Alert className="bg-blue-50 border-blue-200">
          <AlertDescription className="text-blue-900">
            <strong>ℹ️ How it works:</strong>
            <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
              <li>You can only add students from <strong>Grade {teacherGradeLevel}</strong> (your assigned grade)</li>
              <li>Students you add go to a "holding area" - they won't appear on the EA roster until after the next closeout</li>
              <li>After closeout, the admin will "pull" all teacher-assigned students to the active roster</li>
              <li>You can add multiple students at once, but can't add the same student twice in one day</li>
              <li>You must re-add students daily - assignments don't carry over</li>
            </ul>
          </AlertDescription>
        </Alert>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>Add Students to EA Request</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Select Students ({selectedStudentIds.length} selected)</Label>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    placeholder="Search by name or student ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 mb-2"
                  />
                </div>
                <div className="border rounded-lg max-h-80 overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedStudentIds.length === filteredStudents.length && filteredStudents.length > 0}
                            onCheckedChange={toggleSelectAll}
                          />
                        </TableHead>
                        <TableHead>Student Name</TableHead>
                        <TableHead>Student ID</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredStudents.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center py-8 text-slate-500">
                            {myGradeStudents.length === 0 
                              ? `No Grade ${teacherGradeLevel} students in database`
                              : "No students match your search"}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredStudents.map((student) => (
                          <TableRow key={student.id} className="hover:bg-slate-50">
                            <TableCell>
                              <Checkbox
                                checked={selectedStudentIds.includes(student.student_id)}
                                onCheckedChange={() => toggleStudentSelection(student.student_id)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              {student.last_name}, {student.first_name}
                            </TableCell>
                            <TableCell className="font-mono text-sm">{student.student_id}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="subject">Subject (Course) *</Label>
                  <Select value={subject} onValueChange={setSubject}>
                    <SelectTrigger id="subject">
                      <SelectValue placeholder="Select subject" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MATH">MATH</SelectItem>
                      <SelectItem value="ENGLISH">ENGLISH</SelectItem>
                      <SelectItem value="SCIENCE">SCIENCE</SelectItem>
                      <SelectItem value="SOCIAL STUDIES">SOCIAL STUDIES</SelectItem>
                      <SelectItem value="CHINESE">CHINESE</SelectItem>
                      <SelectItem value="SPANISH">SPANISH</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">This will appear in "Course Short Description"</p>
                </div>

                <div>
                  <Label htmlFor="teacher-name">Teacher (Auto-filled)</Label>
                  <Input
                    id="teacher-name"
                    value={teacherName}
                    disabled
                    className="bg-slate-50"
                  />
                  <p className="text-xs text-slate-500 mt-1">Your name appears in "Teacher" column</p>
                </div>
              </div>

              <div>
                <Label htmlFor="assignment-text">Assignment *</Label>
                <Textarea
                  id="assignment-text"
                  placeholder="Why do these students need EA support? (e.g., 'Missing homework assignments', 'Low test scores', 'Needs extra help with fractions')"
                  value={assignmentText}
                  onChange={(e) => setAssignmentText(e.target.value)}
                  className="h-24"
                />
                <p className="text-xs text-slate-500 mt-1">This will appear in the "Assignment" column on the roster</p>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={addMultipleAssignmentsMutation.isPending || selectedStudentIds.length === 0}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add {selectedStudentIds.length} Student{selectedStudentIds.length !== 1 ? 's' : ''} to EA Request
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>My Pending EA Requests ({todaysPending.length} today, {pendingAssignments.length} total)</CardTitle>
          </CardHeader>
          <CardContent>
            {pendingAssignments.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No pending requests</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Grade</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Assignment</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingAssignments.map((assignment) => (
                      <TableRow key={assignment.id} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-sm">
                          {format(new Date(assignment.request_date), 'MM/dd/yyyy')}
                        </TableCell>
                        <TableCell className="font-medium">{assignment.student_name}</TableCell>
                        <TableCell>{assignment.grade_level}</TableCell>
                        <TableCell>
                          <span className="font-semibold text-blue-700">{assignment.subject}</span>
                        </TableCell>
                        <TableCell className="text-sm max-w-xs truncate">
                          {assignment.assignment || '-'}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Pending Pull
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteAssignmentMutation.mutate(assignment.id)}
                            disabled={deleteAssignmentMutation.isPending}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
